import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useMemo} from 'react';
import _ from 'underscore';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

export enum AsyncButtonLoadingBehavior {
  /** Replace the text/icon in the button with a loading spinner */
  SPINNER = 'SPINNER',
  /** Show the loading spinner with the text */
  SPINNER_WITH_TEXT = 'SPINNER_WITH_TEXT',
  /** Disable the button, but keep text/icon */
  DISABLED = 'DISABLED',
}

/** @package */
export interface BaseAsyncButtonProps {
  buttonStyle: ButtonStyle;
  size: ButtonSize;

  /**
   * Optional icon to show in the button.
   *
   * If loadingBehavior is SPINNER/SPINNER_WITH_TEXT, will not be shown while the button is loading.
   */
  icon?: BenchlingIconName;

  /**
   * Button behavior while the onClick is running.
   */
  loadingBehavior: AsyncButtonLoadingBehavior;

  /**
   * Text to show in the button when the button.
   *
   * If loadingBehavior is SPINNER, will not be shown while the button is loading.
   */
  text: ReactNode;

  fontWeight?: number;
  textColor?: string;
  disabled?: boolean;

  /**
   * Async function called when the button is clicked.
   */
  onClick: (e: ElementMouseEvent) => Promise<void>;
}

export interface ControlledAsyncButtonProps extends BaseAsyncButtonProps {
  isLoading: boolean;
}

export function ControlledAsyncButton({
  buttonStyle,
  size,
  icon,
  isLoading,
  loadingBehavior,
  onClick,
  text,
  textColor,
  fontWeight,
  disabled,
  ...props
}: ControlledAsyncButtonProps): JSX.Element {
  return (
    <Button
      buttonStyle={buttonStyle}
      size={size}
      disabled={disabled || isLoading}
      icon={!isLoading || loadingBehavior === AsyncButtonLoadingBehavior.DISABLED ? icon : undefined}
      onClick={onClick}
      text={
        isLoading && loadingBehavior === AsyncButtonLoadingBehavior.SPINNER ? (
          <BenchlingIcon icon="refresh" className="glyphicon-spin" />
        ) : isLoading && loadingBehavior === AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT ? (
          <div>
            <BenchlingIcon icon="refresh" className="glyphicon-spin" />
            <span className={css(styles.textWithSpinner)}>{text}</span>
          </div>
        ) : (
          text
        )
      }
      textColor={textColor}
      fontWeight={fontWeight}
      {...props}
    />
  );
}

const loadingState = makeUseGlobalClientState(false);

interface AsyncButtonProps extends BaseAsyncButtonProps {
  /**
   * If supplied, ID to use to store the loading state in Redux. This is helpful especially for longer running
   * tasks so that if the user navigates away (leading to the AsyncButton being unmounted), then navigates
   * back, the button will still be disabled until the task completes.
   *
   * If not supplied, the loading state will behave as if it is not in Redux (cleared on unmount and reset to
   * false on remount).
   */
  clientStateId?: string;
}

/**
 * Button with an asynchronous onClick handler. Renders a spinner while the onClick is running.
 *
 * @example
 * <AsyncButton
 *   buttonStyle={ButtonStyle.SECONDARY}
 *   size={ButtonSize.MEDIUM}
 *   loadingBehavior={AsyncButtonLoadingBehavior.SPINNER}
 *   onClick={() => TaskRunner.longTask(3, 'seconds')}
 *   text="Run a task"
 * />
 */
export default function AsyncButton({
  buttonStyle,
  size,
  clientStateId,
  icon,
  loadingBehavior,
  onClick,
  text,
  textColor,
  fontWeight,
  disabled,
}: AsyncButtonProps): JSX.Element {
  const id = useMemo(() => clientStateId ?? _.uniqueId('async-button-'), [clientStateId]);
  const [isLoading, setIsLoading] = loadingState.useGlobalClientState(id);

  return (
    <ControlledAsyncButton
      buttonStyle={buttonStyle}
      size={size}
      icon={icon}
      onClick={(e) => {
        setIsLoading(true);
        return onClick(e).finally(() => {
          setIsLoading(false);
        });
      }}
      loadingBehavior={loadingBehavior}
      isLoading={isLoading}
      disabled={disabled}
      text={text}
      textColor={textColor}
      fontWeight={fontWeight}
    />
  );
}

const styles = StyleSheet.create({
  textWithSpinner: {
    marginLeft: 8,
  },
});
